import {
  CSSProperties,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  ReactElement,
} from "react";
import { Icon } from "../Icon/icon";
import { Button } from "../Button/button";
import { Accordion } from "../Accordion/Accordion";
import styles from "../Dropdown/Dropdown.module.scss";
import moreStyles from "./DropdownMenu.module.scss";
import {
  useFloating,
  useClick,
  useHover,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  safePolygon,
  FloatingPortal,
  Placement,
} from "@floating-ui/react";
import { createPortal } from "react-dom";
import { useIsMobile } from "../../utils";
import { Tooltip } from "react-tooltip";

const isReactElementOption = (
  value: SubMenuOption,
): value is ReactElementSubMenuOption => "key" in value;

type RegularSubMenuOption = {
  label: string;
  icon?: string;
  style?: CSSProperties;
  className?: string;
  onSelect: () => void;
  disabled?: boolean;
  group?: string;
};
export type SubMenuContentHandlers = {
  onInteractiveFocus: () => void;
  onInteractiveBlur: () => void;
};

type ReactElementSubMenuOption = {
  key: string | number;
  group?: string;
  content:
    | ReactElement
    | ((
        wrapper: ReactElement,
        handlers?: SubMenuContentHandlers,
      ) => ReactElement);
};

type SubMenuOption = RegularSubMenuOption | ReactElementSubMenuOption;

export interface Option {
  label: string;
  icon?: string;
  style?: CSSProperties;
  className?: string;
  onSelect?: () => void;
  disabled?: boolean;
  subMenu?: SubMenuOption[];
  tooltip?: ReactNode;
}

interface DropdownMenuProps {
  id: string | number;
  label?: string;
  hideLabel?: boolean;
  activator?: ReactNode;
  menuOptions: Option[];
  /**
   * @default "bottom"
   */
  menuPlacement?: "top" | "bottom" | "right" | "left";
  /**
   * @default "start"
   */
  alignMenu?: "start" | "end";
  maxMenuHeight?: number;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  style?: CSSProperties;
  dark?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  menuPortalTarget?: Element;
}

export function DropdownMenu({
  id,
  label,
  hideLabel,
  activator,
  menuOptions,
  menuPlacement = "bottom",
  alignMenu = "start",
  maxMenuHeight,
  disabled,
  isLoading,
  className,
  style,
  dark,
  onOpenChange,
  menuPortalTarget = document.querySelector("main") || document.body,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [referenceWidth, setReferenceWidth] = useState<number | null>(null);
  const isMobile = useIsMobile();
  const [activeSubMenuLabel, setActiveSubMenuLabel] = useState<string | null>(
    null,
  );

  const activeSubMenu = useMemo(() => {
    if (!activeSubMenuLabel) return null;
    const opt = menuOptions.find((o) => o.label === activeSubMenuLabel);
    return opt?.subMenu
      ? { label: activeSubMenuLabel, options: opt.subMenu }
      : null;
  }, [activeSubMenuLabel, menuOptions]);

  const listRef = useRef<HTMLUListElement>(null);

  // Prevent floating-ui's dismiss from closing the menu when clicking inside a submenu portal
  const handleIsOpenChange = (open: boolean, event?: Event) => {
    if (
      !open &&
      event &&
      (event.target as Element)?.closest?.("[data-dropdown-submenu]")
    ) {
      return;
    }
    setIsOpen(open);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleIsOpenChange,
    middleware: [offset(4), flip(), shift()],
    placement: `${menuPlacement}-${alignMenu}` as Placement,
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  // Submenu triggers are not keyboard-navigable
  const navigableOptions = useMemo(
    () => menuOptions.filter((opt) => !opt.subMenu),
    [menuOptions],
  );

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) setActiveSubMenuLabel(null);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (refs.reference.current) {
        setReferenceWidth((refs.reference.current as HTMLElement).offsetWidth);
      }
      const firstEnabled = navigableOptions.findIndex((opt) => !opt.disabled);
      setHighlightedIndex(firstEnabled === -1 ? 0 : firstEnabled);
      listRef.current?.focus({ preventScroll: true });
    }
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, refs.reference, navigableOptions]);

  const activeSubMenuGrouped = useMemo(() => {
    if (!activeSubMenu) return { groups: [], ungrouped: [] };
    const groupedMap: Record<string, SubMenuOption[]> = {};
    const groupOrder: string[] = [];
    const ungrouped: SubMenuOption[] = [];
    activeSubMenu.options.forEach((opt) => {
      if (opt.group) {
        if (!groupedMap[opt.group]) {
          groupedMap[opt.group] = [];
          groupOrder.push(opt.group);
        }
        groupedMap[opt.group].push(opt);
      } else {
        ungrouped.push(opt);
      }
    });
    return {
      groups: groupOrder.map(
        (g) => [g, groupedMap[g]] as [string, SubMenuOption[]],
      ),
      ungrouped,
    };
  }, [activeSubMenu]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen || navigableOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev;
          do {
            next = (next + 1) % navigableOptions.length;
          } while (navigableOptions[next].disabled && next !== prev);
          return next;
        });
        break;

      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev;
          do {
            next =
              (next - 1 + navigableOptions.length) % navigableOptions.length;
          } while (navigableOptions[next].disabled && next !== prev);
          return next;
        });
        break;
      case "Enter": {
        e.preventDefault();
        const option = navigableOptions[highlightedIndex];
        if (option && !option.disabled && option.onSelect) {
          option.onSelect();
          setIsOpen(false);
        }
        break;
      }
      case "Escape":
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  const renderSubMenuOption = (opt: SubMenuOption) =>
    isReactElementOption(opt) ? (
      typeof opt.content === "function" ? (
        opt.content(<li key={opt.key}></li>)
      ) : (
        opt.content
      )
    ) : (
      <li
        key={opt.label}
        role="option"
        className={`
        ${styles.dropdownOption}
        ${moreStyles.dropdownOption}
        ${opt.disabled ? styles.disabled : ""}
        ${opt.className || ""}
      `}
        style={opt.style}
        onMouseDown={(e) => {
          e.preventDefault();
          if (!opt.disabled) {
            opt.onSelect();
            setIsOpen(false);
          }
        }}
      >
        {opt.icon && <Icon name={opt.icon} />}
        {opt.label}
      </li>
    );

  const renderOption = (option: Option, index: number) => {
    if (option.subMenu) {
      if (isMobile) {
        return (
          <li
            key={option.label}
            id={`option-${id}-${index}`}
            role="option"
            aria-selected={false}
            className={`
              ${styles.dropdownOption}
              ${moreStyles.dropdownOption}
              ${option.disabled ? styles.disabled : ""}
              ${option.className || ""}
            `}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ...option.style,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              if (!option.disabled) {
                setActiveSubMenuLabel(option.label);
              }
            }}
          >
            {option.icon && <Icon name={option.icon} />}
            <span style={{ flex: 1 }}>{option.label}</span>
            <Icon name="chevron_right" style={{ fontSize: "16px" }} />
          </li>
        );
      }
      return (
        <SubMenuTriggerItem
          key={option.label}
          option={option as Option & { subMenu: SubMenuOption[] }}
          dark={dark}
          onClose={() => setIsOpen(false)}
        />
      );
    }

    const navIndex = navigableOptions.indexOf(option);
    return (
      <li
        key={option.label}
        id={`option-${id}-${index}`}
        role="option"
        aria-selected={highlightedIndex === navIndex}
        className={`
          ${styles.dropdownOption}
          ${moreStyles.dropdownOption}
          ${highlightedIndex === navIndex ? styles.highlighted : ""}
          ${option.disabled ? styles.disabled : ""}
          ${option.className || ""}
        `}
        style={option.style}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!option.disabled && option.onSelect) {
            option.onSelect();
            setIsOpen(false);
          }
        }}
        onMouseEnter={() => {
          if (!option.disabled) setHighlightedIndex(navIndex);
        }}
      >
        {option.icon && <Icon name={option.icon} />}
        {option.label}
      </li>
    );
  };

  return (
    <div
      id={id.toString()}
      className={`${styles.container} ${className}`}
      style={style}
    >
      <div
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen((prev) => (disabled ? false : !prev));
        }}
        id={`control-${id}`}
        aria-controls={`menu-${id}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        ref={refs.setReference}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        {...getReferenceProps()}
      >
        {activator || (
          <Button
            icon="more_vert"
            dark={!dark}
            disabled={disabled}
            isLoading={isLoading}
          >
            <span className={!label || hideLabel ? "sr-only" : ""}>
              {label ?? "Open menu"}
            </span>
          </Button>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            id={`menu-${id}`}
            role="listbox"
            aria-labelledby={`control-${id}`}
            className={`${styles.dropdownMenu} ${dark && styles.dark} ${styles[menuPlacement]} ${moreStyles.dropdownMenu}`}
            style={{
              ...(maxMenuHeight ? { maxHeight: maxMenuHeight } : {}),
              ...(referenceWidth ? { minWidth: referenceWidth } : {}),
              ...floatingStyles,
              zIndex: 150,
            }}
            {...getFloatingProps()}
          >
            {isMobile && activeSubMenu ? (
              <ul
                tabIndex={0}
                ref={listRef}
                onKeyDown={handleKeyDown}
                style={{ outline: "none" }}
              >
                <li
                  className={`${styles.dropdownOption} ${moreStyles.dropdownOption}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: "bold",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setActiveSubMenuLabel(null);
                  }}
                >
                  <Icon
                    name="chevron_left"
                    style={{ fontSize: "16px", marginRight: "4px" }}
                  />
                  {activeSubMenu.label}
                </li>
                {activeSubMenuGrouped.groups.map(
                  ([groupName, groupOptions]) => (
                    <Fragment key={`group-${groupName}`}>
                      <li
                        className={`${styles.dropdownOption} ${moreStyles.dropdownOption}`}
                        style={{
                          opacity: 0.6,
                          cursor: "default",
                          fontWeight: "bold",
                        }}
                      >
                        {groupName}
                      </li>
                      {groupOptions.map(renderSubMenuOption)}
                    </Fragment>
                  ),
                )}
                {activeSubMenuGrouped.ungrouped.map(renderSubMenuOption)}
              </ul>
            ) : (
              <ul
                tabIndex={0}
                ref={listRef}
                onKeyDown={handleKeyDown}
                style={{ outline: "none" }}
              >
                {menuOptions.map((option, index) =>
                  renderOption(option, index),
                )}
              </ul>
            )}
          </div>,
          menuPortalTarget,
        )}
      {isOpen &&
        menuOptions.map((option, index) =>
          option.tooltip
            ? createPortal(
                <Tooltip
                  key={`tooltip-${id}-${index}`}
                  anchorSelect={`#option-${id}-${index}`}
                  positionStrategy="fixed"
                >
                  {option.tooltip}
                </Tooltip>,
                document.body,
              )
            : null,
        )}
    </div>
  );
}

function SubMenuTriggerItem({
  option,
  dark,
  onClose,
}: {
  option: Option & { subMenu: SubMenuOption[] };
  dark?: boolean;
  onClose: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [interactiveFocused, setInteractiveFocused] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "right-start",
    middleware: [
      offset({ mainAxis: 0, alignmentAxis: -4 }),
      flip(),
      shift({ padding: 10 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const hover = useHover(context, {
    handleClose: safePolygon(),
    delay: { open: 75 },
    enabled: !interactiveFocused,
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    dismiss,
  ]);

  const interactiveHandlers: SubMenuContentHandlers = {
    onInteractiveFocus: () => setInteractiveFocused(true),
    onInteractiveBlur: () => setInteractiveFocused(false),
  };

  const { groupedSubMenuOptions, ungroupedSubMenuOptions } = useMemo(() => {
    const groupedMap: Record<string, SubMenuOption[]> = {};
    const groupOrder: string[] = [];
    const ungrouped: SubMenuOption[] = [];

    option.subMenu.forEach((opt) => {
      if (opt.group) {
        if (!groupedMap[opt.group]) {
          groupedMap[opt.group] = [];
          groupOrder.push(opt.group);
        }
        groupedMap[opt.group].push(opt);
      } else {
        ungrouped.push(opt);
      }
    });

    return {
      groupedSubMenuOptions: groupOrder.map(
        (g) => [g, groupedMap[g]] as [string, SubMenuOption[]],
      ),
      ungroupedSubMenuOptions: ungrouped,
    };
  }, [option.subMenu]);

  return (
    <li
      ref={refs.setReference}
      {...getReferenceProps()}
      className={`${styles.dropdownOption} ${moreStyles.dropdownOption} ${isOpen ? styles.highlighted : ""}`}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {option.icon && <Icon name={option.icon} />}
      <span style={{ flex: 1 }}>{option.label}</span>
      <Icon name="chevron_right" style={{ fontSize: "16px" }} />

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              visibility: "visible",
              width: "max-content",
              minWidth: "150px",
              zIndex: 150,
            }}
            className={`${styles.dropdownMenu} ${dark ? styles.dark : ""} ${styles.bottom} ${moreStyles.dropdownMenu}`}
            data-dropdown-submenu=""
            {...getFloatingProps({
              onClick: (e) => e.stopPropagation(),
            })}
          >
            <ul>
              {groupedSubMenuOptions.map(([groupName, groupOptions]) => (
                <li
                  key={`group-${groupName}`}
                  className={styles.groupContainer}
                >
                  <Accordion
                    id={`submenu-group-${groupName}`}
                    header={
                      <span className={styles.groupHeader}>{groupName}</span>
                    }
                    body={
                      <ul className={styles.groupList}>
                        {groupOptions.map((opt) =>
                          isReactElementOption(opt) ? (
                            typeof opt.content === "function" ? (
                              opt.content(
                                <li key={opt.key}></li>,
                                interactiveHandlers,
                              )
                            ) : (
                              opt.content
                            )
                          ) : (
                            <li
                              key={opt.label}
                              role="option"
                              className={`
        ${styles.dropdownOption}
        ${moreStyles.dropdownOption}
        ${opt.disabled ? styles.disabled : ""}
        ${opt.className || ""}
      `}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (opt.disabled) return;
                                opt.onSelect();
                                onClose();
                              }}
                            >
                              {opt.icon && <Icon name={opt.icon} />}
                              <span>{opt.label}</span>
                            </li>
                          ),
                        )}
                      </ul>
                    }
                    expanded={true}
                    onToggle={() => {}}
                  />
                </li>
              ))}
              {ungroupedSubMenuOptions.map((opt) =>
                isReactElementOption(opt) ? (
                  typeof opt.content === "function" ? (
                    opt.content(<li key={opt.key}></li>, interactiveHandlers)
                  ) : (
                    opt.content
                  )
                ) : (
                  <li
                    key={opt.label}
                    role="option"
                    className={`
        ${styles.dropdownOption}
        ${moreStyles.dropdownOption}
        ${opt.disabled ? styles.disabled : ""}
        ${opt.className || ""}
      `}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (opt.disabled) return;
                      opt.onSelect();
                      onClose();
                    }}
                  >
                    {opt.icon && <Icon name={opt.icon} />}
                    <span>{opt.label}</span>
                  </li>
                ),
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </li>
  );
}
